<template>
    <div>
        <b-modal
            id="modal-import-errors-csv"
            ref="modal-import-errors-csv"
            :ok-title="$t('Close')"
            centered
            content-class="mh-100 mw-100"
            dialog-class="w-auto mw-lg-90"
            footer-class="flex-nowrap"
            header-close-variant="light"
            ok-only
            scrollable
        >
            <template #modal-header="{ close }">
                <h3 class="text-danger d-flex align-items-center">
                    <font-awesome-icon class="mr-50" icon="circle-exclamation" size="sm" />
                    {{ $t("contacts.lists.wizard.panel-import-error-title") }}
                </h3>
                <button
                    aria-label="Close"
                    class="close text-light"
                    type="button"
                    @click="$bvModal.hide('modal-import-errors-csv')"
                >
                    ×
                </button>
            </template>
            <p class="text-dark">{{ $t("contacts.lists.wizard.panel-import-error-description") }}</p>
            <b-table
                :fields="importErrorsFields"
                :items="importErrors"
                :no-border-collapse="false"
                :no-sort-reset="true"
                :sticky-header="true"
                responsive
            />
        </b-modal>

        <b-modal v-if="showPanelCsv" ref="modal-info-csv" centered header-close-variant="light" hide-footer size="xl">
            <template #modal-title>{{ $t("contacts.lists.wizard.panel-import-title") }}</template>
            <p>{{ $t("contacts.lists.wizard.panel-import-description") }}</p>
            <b-button :href="downloadUrl()" target="_blank" variant="primary">{{
                $t("contacts.lists.wizard.panel-import-button-template")
            }}</b-button>
            <br /><br />
            <strong class="text-dark">{{ $t("contacts.lists.wizard.panel-instructions-title") }}</strong>
            <ul class="text-dark small">
                <li>{{ $t("contacts.lists.wizard.panel-instructions-bullet1") }}</li>
                <li>{{ $t("contacts.lists.wizard.panel-instructions-bullet2") }}</li>
                <li>{{ $t("contacts.lists.wizard.panel-instructions-bullet3") }}</li>
                <li>{{ $t("contacts.lists.wizard.panel-instructions-bullet4") }}</li>
                <li>{{ $t("contacts.lists.wizard.panel-instructions-bullet5") }}</li>
                <li>{{ $t("contacts.lists.wizard.panel-instructions-bullet6") }}</li>
                <li>{{ $t("contacts.lists.wizard.panel-instructions-bullet7") }}</li>
            </ul>
            <div class="d-flex justify-content-between mb-2">
                <b-form-checkbox
                    id="checkbox-panel-instructions"
                    v-model="hidePanelCsv"
                    name="checkbox-panel-instructions"
                    unchecked-value="false"
                    value="true"
                    @change="setInstructionsVisibility"
                >
                    {{ $t("contacts.lists.wizard.panel-import-dont-show-again") }}
                </b-form-checkbox>
                <div>
                    <b-button class="mr-1" variant="light" @click="resetType()">{{ $t("Cancel") }}</b-button>
                    <b-button variant="primary" @click="closeModal()">{{ $t("Next") }}</b-button>
                </div>
            </div>
        </b-modal>

        <!-- STEP 0 / Select type (import or select) -->
        <b-container v-show="activeTab === 0" id="step0" class="tabcontent p-0" fluid>
            <b-row align-h="center">
                <b-col lg="8" md="10">
                    <b-card-group deck>
                        <b-card>
                            <template #header>
                                <img class="w-100 mx-auto" src="@/assets/images/pages/contacts-csv.png" />
                            </template>
                            <b-card-text class="text-secondary">
                                <h4>{{ $t("contacts.lists.wizard.select-type-csv-title") }}</h4>
                                <p
                                    v-html="
                                        this.$t('contacts.lists.wizard.select-type-csv-description', {
                                            downloadUrl: downloadUrl(),
                                        })
                                    "
                                ></p
                            ></b-card-text>
                            <template #footer>
                                <b-button class="mb-50 font-small-3" variant="primary" @click="setType('csv')"
                                    >{{ $t("contacts.lists.wizard.select-type-csv-button") }}
                                </b-button>
                            </template>
                        </b-card>

                        <b-card>
                            <template #header>
                                <img class="w-100 mx-auto" src="@/assets/images/pages/contacts-select.png" />
                            </template>
                            <b-card-text class="text-secondary">
                                <h4>{{ $t("contacts.lists.wizard.select-type-existing-title") }}</h4>
                                <p v-html="this.$t('contacts.lists.wizard.select-type-existing-description')"></p>
                            </b-card-text>
                            <template #footer>
                                <b-button class="mb-50 font-small-3" variant="primary" @click="setType('existing')"
                                    >{{ $t("contacts.lists.wizard.select-type-existing-button") }}
                                </b-button>
                            </template>
                        </b-card>
                    </b-card-group>
                </b-col>
            </b-row>
        </b-container>

        <!-- Tab/pages content -->
        <div class="content-block mt-0 pb-0">
            <!-- Step 1 | Import CSV and/or select contacts -->
            <div v-show="activeTab === 1" id="step1" class="tabcontent">
                <template v-if="contactsList.id">
                    <AppContactListsExisting
                        v-if="!editMode"
                        :selected-contacts="contactsList.contacts.map((contact) => contact.id)"
                        @update-selection="updateSelection"
                    />
                    <AppContactListEditContacts v-else :contact-list-id="contactsList.id" />
                </template>
                <template v-else>
                    <div v-if="activeType === 'csv'">
                        <AppContactListsImport
                            @update-selection="updateSelection"
                            @update-existing-contacts="updateExistingContacts"
                            @open-panel-csv="openPanelCsv"
                            @open-modal="openModal"
                            @error-update="(status) => (hasImportErrors = status)"
                        />
                    </div>
                    <div v-if="activeType === 'existing'">
                        <AppContactListsExisting @update-selection="updateSelection" />
                    </div>
                </template>
            </div>

            <!-- Step 2 | Contact list details -->
            <b-container v-show="activeTab === 2" id="step2" class="tabcontent" fluid>
                <b-row align-h="center">
                    <b-col cols="12" md="10">
                        <h2>{{ $t("contacts.lists.wizard.list-detail-title") }}</h2>
                        <p>{{ $t("contacts.lists.wizard.list-details-description") }}</p>

                        <b-form-group
                            :label="`${$t('contacts.lists.wizard.list-details-label-name')}*`"
                            content-cols-lg="7"
                            content-cols-sm
                            label-cols-lg="3"
                            label-cols-sm="4"
                            label-for="name"
                            name="name"
                        >
                            <validation-provider #default="{ errors }" name="name" rules="required">
                                <b-form-input
                                    id="name"
                                    v-model="contactsList.name"
                                    :state="errors.length > 0 ? false : null"
                                    maxlength="50"
                                    type="text"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>

                        <b-form-group
                            :label="`${$t('contacts.lists.wizard.list-details-label-description')}`"
                            content-cols-lg="7"
                            content-cols-sm
                            label-cols-lg="3"
                            label-cols-sm="4"
                            label-for="description"
                            name="description"
                        >
                            <b-form-input
                                id="description"
                                v-model="contactsList.description"
                                :state="errors.length > 0 ? false : null"
                                type="text"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                        <div v-if="activeType === 'existing' || activeType === 'csv'">
                            <b-form-group
                                :label="`${$t('contacts.lists.wizard.list-details-label-contact-labels')}`"
                                content-cols-lg="7"
                                content-cols-sm
                                label-cols-lg="3"
                                label-cols-sm="4"
                                label-for="description"
                                name="description"
                            >
                                <contact-labels
                                    v-model="contactsList.labels"
                                    :contact-id="contactsList.id"
                                    @updateLabels="updateLabels"
                                />
                            </b-form-group>
                        </div>
                    </b-col>
                </b-row>
            </b-container>

            <!-- Step 2 | Contact list overview before saving -->
            <b-container v-show="activeTab === 3" id="step3" class="tabcontent p-0" fluid>
                <b-row align-h="center">
                    <b-col>
                        <h2>{{ $t("Overview") }}</h2>
                    </b-col>
                </b-row>
                <app-contact-lists-overview :contact-list="contactsList" />
            </b-container>
        </div>

        <!-- Timeline -->
        <div
            v-if="!hideTimeline"
            id="time-line-nav-container"
            class="d-flex justify-content-between align-items-center mt-4 mb-2"
        >
            <div class="time-line-nav left">
                <b-button variant="light" @click="buttonAction('prev')">{{ buttonText("prev") }}</b-button>
            </div>
            <div id="time-line" class="d-flex flex-grow-1 justify-content-center align-items-center mx-4">
                <div :class="{ disabled: activeTab > 1 }" class="step d-flex align-items-center">
                    <b-avatar
                        v-b-tooltip.hover
                        :class="{ active: activeTab < 2 }"
                        :disabled="!tabIsActive(1)"
                        :title="$t('contacts.lists.wizard.time-line-step-1-title')"
                        rounded
                        size="24px"
                        text="1"
                        variant="primary"
                    />
                    <small class="ml-1">{{ $t("contacts.lists.wizard.time-line-step-1-title") }}</small>
                    <font-awesome-icon class="mx-4" icon="caret-right" />
                </div>

                <div :class="{ disabled: activeTab != 2 }" class="step d-flex align-items-center">
                    <b-avatar
                        v-b-tooltip.hover
                        :class="{ active: activeTab === 2 }"
                        :disabled="!tabIsActive(2)"
                        :title="$t('contacts.lists.wizard.time-line-step-2-title')"
                        rounded
                        size="24px"
                        text="2"
                        variant="primary"
                    />
                    <small class="ml-50">{{ $t("contacts.lists.wizard.time-line-step-2-title") }}</small>
                    <font-awesome-icon class="mx-3" icon="caret-right" />
                </div>

                <div :class="{ disabled: activeTab != 3 }" class="step d-flex align-items-center">
                    <b-avatar
                        v-b-tooltip.hover
                        :class="{ active: activeTab === 3 }"
                        :disabled="!tabIsActive(3)"
                        :title="$t('contacts.lists.wizard.time-line-step-3-title')"
                        rounded
                        size="24px"
                        text="3"
                        variant="primary"
                    />
                    <small class="ml-1">{{ $t("contacts.lists.wizard.time-line-step-3-title") }}</small>
                </div>
            </div>
            <div class="time-line-nav right">
                <b-button v-if="activeTab === 0" disabled="disabled" variant="primary">
                    {{ buttonText("next") }}
                </b-button>
                <b-button v-else :disabled="!buttonEnabled || isSaving" variant="primary" @click="buttonAction('next')">
                    <b-spinner v-if="isSaving" small />
                    {{ buttonText("next") }}
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
import {
    BContainer,
    BRow,
    BCol,
    BCardGroup,
    BCard,
    BButton,
    BAvatar,
    BCardText,
    BModal,
    BFormCheckbox,
    BTooltip,
    BFormGroup,
    BFormInput,
    BSpinner,
    VBTooltip,
    BTable,
    BBadge,
    VBModal,
} from "bootstrap-vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { required } from "@validations";
import AppContactListsImport from "./contactListsImport";
import AppContactListsExisting from "./contactListsExisting";
import AppContactListEditContacts from "./contactListEditContacts.vue";
import AppContactListsOverview from "./contactListsOverview";

import * as lodash from "lodash";
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import i18n from "@/libs/i18n";
import toastificationContent from "@/layouts/components/toastification/toastificationContent";
import useContactsLists from "@/composables/useContactsLists";
import { watch, ref, onMounted } from "@vue/composition-api";
import contactLabels from "./contactLabels.vue";

export default {
    components: {
        BBadge,
        BTable,
        BContainer,
        BRow,
        BCol,
        BCardGroup,
        BCard,
        BButton,
        BAvatar,
        BCardText,
        BModal,
        BFormCheckbox,
        BTooltip,
        BFormGroup,
        BFormInput,
        BSpinner,
        ValidationObserver,
        ValidationProvider,
        AppContactListsImport,
        AppContactListsExisting,
        AppContactListEditContacts,
        AppContactListsOverview,
        contactLabels,
    },
    directives: {
        "b-tooltip": VBTooltip,
        "b-modal": VBModal,
    },
    props: {
        currentContactListId: {
            type: Number,
            default() {
                return null;
            },
        },
        originalContactListContacts: {
            type: Array,
            required: false,
        },
        list: {
            type: Object,
            required: false,
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        hideTimeline: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            initiallySelectedContacts: [], // The originally selected contacts in the list
            saveEnabled: true,
            activeType: null,
            showPanelCsv: true,
            hidePanelCsv: false,
            isSaving: false,
            hasImportErrors: false,
            errors: [],
            required,
            contactListLabels: [],
            importErrors: [],
            importErrorsFields: [],
        };
    },
    computed: {
        buttonEnabled() {
            // Only allowed next step when there are contacts selected
            if (this.activeTab === 1 && this.contactsList.contacts?.length > 0 && !this.hasImportErrors) {
                return true;
            }

            // Only allowed final step when list has a name
            if (this.activeTab === 2 && this.contactsList.name && this.contactsList.name.length > 0) {
                return true;
            }

            if (this.activeTab === 3) {
                return true;
            }

            return false;
        },
    },
    methods: {
        setType(type) {
            this.activeType = type;
            this.activeTab = 1;

            this.showPanelCsv = true;

            if (localStorage.getItem("hidePanelCsv") === "true") {
                this.showPanelCsv = false;
            } else {
                this.openModal();
            }
            this.contactsList.importedContactData = true;

            if (this.activeType !== "csv") {
                this.contactsList.importedContactData = false;
            }
        },
        openModal() {
            if (this.activeType === "csv") {
                this.$refs["modal-info-csv"].show();
            }
        },
        closeModal() {
            this.$refs["modal-info-csv"].hide();
        },
        openPanelCsv() {
            this.showPanelCsv = true;
            this.hidePanelCsv = localStorage.getItem("hidePanelCsv");
        },
        closePanelCsv() {
            this.showPanelCsv = false;
        },
        resetType() {
            this.activeType = null;
            this.activeTab = 0;

            this.closeModal();
        },
        updateLabels(labels) {
            this.contactListLabels = labels;
        },
        save() {
            this.isSaving = true;

            // Here we prepare a new request object containing the contactlist and all it's data and options for the backend.
            // We can't just send the contactlist because we don't want any (future) options in the contactlist object itself
            // and the system has no idea what is contact metadata yet and such.
            const contactListRequestObject = {
                contactLists: JSON.parse(JSON.stringify(this.contactsList)),
                updateExisting: true,
                importedContactData: false,
            };

            if (contactListRequestObject.contactLists.updateExistingContacts === false) {
                contactListRequestObject.updateExisting = false;
            }

            // Those keys are not vars of the contactList object, remove them
            delete contactListRequestObject.contactLists.updateExistingContacts;
            delete contactListRequestObject.contactLists.importedContactData;

            if (this.contactsList.importedContactData) {
                contactListRequestObject.importedContactData = true;

                const defaultContactFields = [
                    "name",
                    "email",
                    "phone",
                    "street",
                    "houseNumber",
                    "zipCode",
                    "labels",
                    "city",
                    "province",
                    "countryCode",
                    "reference",
                    "company",
                    "jobTitle",
                    "metaData",
                ];

                contactListRequestObject.contactLists.contacts = this.contactsList.contacts.map((contactField) => {
                    return {
                        // All properties that are NOT in contactFields or are `___rowId___` will become a property of metadata.
                        metadata: lodash.map(
                            lodash.omit(contactField, [...defaultContactFields, "___rowId___"]),
                            (value, key) => ({ name: key, value: value })
                        ),
                        // All properties that are in contactFields will become root elements of the returned object
                        ...lodash.pick(contactField, defaultContactFields),
                    };
                });
            }

            contactListRequestObject.contactLists.contacts.map((contact) => {
                this.contactListLabels.value?.map((label) => {
                    if (contact.labels?.every((contactLabel) => label.id !== contactLabel.labelId)) {
                        contact.labels.push(label);
                    }
                });
            });

            this.saveContactList(contactListRequestObject);
        },
        update() {
            this.isSaving = true;
            const selectedContacts = lodash.map(this.contactsList.contacts, (contact) => {
                return { id: contact.id };
            });

            const contactListRequestObject = {
                id: this.contactsList.id,
                name: this.contactsList.name,
                description: this.contactsList.description,
                contacts: selectedContacts,
            };
            // we will create an array with only the contacts to remove
            const contactsToRemove = this.originalContactListContacts.filter(
                (contact) => !this.contactsList.contacts.some((c) => c.id === contact.id)
            );
            if (contactsToRemove.length > 0) {
                axios
                    .get("/campaigns", {
                        params: {
                            id: contactsToRemove.map((contact) => contact.id),
                            status: "pending",
                        },
                    })
                    .then((response) => {
                        if (response.data.length > 0) {
                            this.$bvModal
                                .msgBoxConfirm(i18n.t("contacts.remove-contact-engage.confirmation"), {
                                    title: i18n.t("contacts.remove-contact-engage.title"),
                                    okTitle: i18n.t("confirm.remove"),
                                    cancelTitle: i18n.t("contacts.remove-contact-engage.cancel"),
                                    cancelVariant: "light",
                                    headerCloseVariant: "light",
                                    hideHeaderClose: false,
                                    centered: true,
                                })
                                .then((result) => {
                                    this.saveContactList(contactListRequestObject, result);
                                });
                        }
                    });
            } else {
                this.saveContactList(contactListRequestObject);
            }
        },
        saveContactList(contactListRequestObject, removePendingEngage = false) {
            if (contactListRequestObject.id) {
                axios
                    .patch(`/contacts-lists/${contactListRequestObject.id}`, {
                        ...contactListRequestObject,
                        removePendingEngage,
                    })
                    .then(() => {
                        this.toast({
                            component: toastificationContent,
                            props: {
                                title: i18n.t("toast.update.title"),
                                text: i18n.t("toast.update.success", {
                                    entity: i18n.t("contacts.lists.contacts-lists"),
                                }),
                                icon: "check",
                                variant: "success",
                            },
                        });

                        return this.$emit("finished", this.contactsList);
                    })
                    .catch(() => {
                        this.toast({
                            component: toastificationContent,
                            props: {
                                title: i18n.t("toast.create.title"),
                                text: i18n.t(`contacts.lists.errors.${notification}`),
                                icon: "times",
                                variant: "danger",
                            },
                        });
                    })
                    .finally(() => (this.isSaving = false));
            } else {
                axios
                    .post("/contacts-lists", contactListRequestObject)
                    .then(() => {
                        this.toast({
                            component: toastificationContent,
                            props: {
                                title: i18n.t("toast.create.title"),
                                text: i18n.t("toast.create.success", {
                                    entity: i18n.t("contacts.lists.contacts-lists"),
                                }),
                                icon: "check",
                                variant: "success",
                            },
                        });

                        return this.$emit("finished", this.contactsList);
                    })
                    .catch((error) => {
                        // We can have two different states on error
                        // If we have a 409 Conflict error we want to show a modal,
                        // else show a simple notification
                        if (error.response.data.errorCode === 409) {
                            this.importErrorsFields = [
                                { key: "csvRow", label: i18n.t("contacts.lists.table-column-csv-row"), sortable: true },
                                {
                                    key: "name",
                                    label: i18n.t("entities.contact.Name"),
                                    sortable: true,
                                },
                                {
                                    key: "phone",
                                    label: i18n.t("entities.contact.Phone"),
                                    sortable: true,
                                },
                                { key: "email", label: i18n.t("entities.contact.Email"), sortable: true },
                                { key: "errorMessage", label: i18n.t("contacts.lists.table-column-error-message") },
                            ];

                            this.importErrors = error.response.data.payload.errors.map((error) => {
                                let errorMessage;
                                let _cellVariants = {};

                                switch (error.message) {
                                    case "Changing a contacts phone number is not allowed":
                                        errorMessage = i18n.t("contacts.lists.errors.not-allowed-change-phone-number");
                                        _cellVariants = { phone: "danger" };
                                        break;
                                    case "Changing a contacts email address is not allowed":
                                        errorMessage = i18n.t("contacts.lists.errors.not-allowed-change-email-address");
                                        _cellVariants = { email: "danger" };
                                        break;
                                    default:
                                        errorMessage = i18n.t("contacts.lists.errors.default-conflict");
                                }

                                const contact = this.contactsList.contacts[error.index];

                                return {
                                    csvRow: error.index + 2, // +2 Because CSV rows start counting at number 1, and the first row is column names
                                    name: contact?.name,
                                    phone: contact?.phone,
                                    email: contact?.email,
                                    errorMessage,
                                    _cellVariants,
                                };
                            });

                            this.$refs["modal-import-errors-csv"].show();
                            return;
                        }

                        let notification = "save_generic";

                        switch (error.response.data.message) {
                            case "Entity not matching the data to successfully execute QueryBuilder":
                                notification = "querybuilder_unprocessable_entity";
                                break;
                            case "Something went wrong with QueryBuilder":
                                notification = "querybuilder_generic";
                                break;
                        }

                        this.toast({
                            component: toastificationContent,
                            props: {
                                title: i18n.t("toast.create.title"),
                                text: i18n.t(`contacts.lists.errors.${notification}`),
                                icon: "times",
                                variant: "danger",
                            },
                        });
                    })
                    .finally(() => (this.isSaving = false));
            }
        },
        tabIsActive(step) {
            // Active tab 0 is the page where the user selects the way how to select it's contacts, by CSV or list.
            if (this.activeTab === 0) {
                return false;
            }

            switch (step) {
                case 1:
                case 2: // For now we set step 2 active too, because we can't import/select any contacts yet
                    return true;
                // This is the overview, we need contacts and a name
                case 3:
                    if (this.contactsList.name && this.contactsList.name.length > 0) {
                        return true;
                    }
                    break;
            }

            return false;
        },
        updateSelection(value) {
            this.contactsList.contacts = value;
        },
        updateExistingContacts(boolean) {
            this.contactsList.updateExistingContacts = boolean;
        },
        buttonAction(direction) {
            // Left button action
            if (direction === "prev") {
                if (this.activeTab === 0 || (this.activeTab === 1 && this.currentContactListId)) {
                    // Back to the overview
                    this.$emit("canceled");
                    return;
                }

                return --this.activeTab;
            }

            // Right button action
            if (this.activeTab === 3) {
                // Back to the overview
                if (typeof this.contactsList.id === "undefined") {
                    this.save();
                } else {
                    this.update();
                }
            }

            if (this.activeTab < 3) {
                return ++this.activeTab;
            }
        },
        buttonText(direction) {
            // Left button text
            if (direction === "prev") {
                if (this.activeTab === 0 || (this.activeTab === 1 && this.currentContactListId)) {
                    return this.$t("Cancel");
                }

                return this.$t("Back");
            }

            // Right button text
            if (this.activeTab === 3) {
                if (this.contactsList.id) {
                    return this.$t("Update");
                }

                return this.$t("Save");
            }

            return this.$t("Next");
        },
        setInstructionsVisibility() {
            if (this.hidePanelCsv === "true") {
                localStorage.setItem("hidePanelCsv", "true");
                return;
            }

            localStorage.removeItem("hidePanelCsv");
        },
        downloadUrl() {
            return `${process.env.VUE_APP_MFE_STATIC_ENDPOINT}/files/csv/saysimple-csv-template-contacts-import.csv`;
        },
    },
    setup(props) {
        const toast = useToast();
        const activeTab = ref(0);

        const { contactsList } = useContactsLists();

        watch(contactsList, async (list) => {
            if (list.id) {
                activeTab.value = 1;
            }
        });

        onMounted(() => {
            if (contactsList.value.id) {
                activeTab.value = 1;
            }
        });

        return {
            toast,
            contactsList,
            activeTab,
        };
    },
};
</script>

<style lang="scss" scoped>
#step0 {
    img {
        max-width: 250px;
    }
}
</style>
